import React from 'react';
import { Trophy, Crown, ExternalLink, Sparkles, Info } from 'lucide-react';
import { JUDGES_2021, CATEGORIES_2021 } from '../data/winners2021';

export default function Winners2021Page() {
  return (
    <main className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8" data-testid="winners-2021-page">
      <div className="max-w-5xl mx-auto">
        {/* Hero */}
        <section className="text-center mb-16">
          <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80 mb-4">2021 Travel Awards &amp; TWBL Awards</p>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-medium text-white mb-6 leading-tight">
            Travel Awards
            <br />
            <span className="text-gradient-gold">Winners &amp; Finalists</span>
          </h1>
        </section>

        {/* Panel of Judges */}
        <section className="mb-20 dashboard-card p-6 sm:p-10" data-testid="panel-of-judges">
          <div className="flex items-center gap-3 mb-6">
            <Crown className="w-5 h-5 text-[#D4AF37]" />
            <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80">
              2021 Travel Awards &amp; TWBL Awards
            </p>
          </div>
          <h2 className="font-['Playfair_Display'] text-3xl font-medium text-white mb-4">
            Panel of Judges
          </h2>
          <p className="text-white/60 max-w-3xl leading-relaxed mb-8">
            A specialist panel of judges is invited to vote on our Nominees each year,
            with the panel of judges selected by the Travel Awards International Advisory
            Board of Directors.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {JUDGES_2021.map((j, i) => (
              <div
                key={i}
                className="flex gap-4 p-4 rounded-xl bg-[#0A0A0A] border border-white/5 hover:border-[#D4AF37]/30 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8960F] flex items-center justify-center shrink-0">
                  <span className="text-sm font-semibold text-[#0A0A0A]">
                    {j.name.split(' ').slice(-1)[0].charAt(0)}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="text-white font-medium">{j.name}</p>
                  <p className="text-white/50 text-sm mt-0.5">{j.title}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Finalists Intro */}
        <div className="text-center mb-12">
          <Sparkles className="w-6 h-6 text-[#D4AF37] mx-auto mb-4" />
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-medium text-white">
            Let's take a look at the 2021 Finalists &amp; Winners
          </h2>
        </div>

        {/* Categories */}
        <div className="space-y-6">
          {CATEGORIES_2021.map((cat) => (
            <CategoryCard key={cat.id} category={cat} />
          ))}
        </div>
      </div>
    </main>
  );
}

function CategoryCard({ category }) {
  return (
    <article
      className="dashboard-card overflow-hidden"
      data-testid={`category-${category.id}`}
    >
      {/* Winner block */}
      <div className="p-6 sm:p-8 border-b border-white/5 bg-gradient-to-r from-[#D4AF37]/5 to-transparent">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="uppercase text-[10px] tracking-[0.3em] text-[#D4AF37]/80 mb-2">
              {category.title}
            </p>
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="w-4 h-4 text-[#D4AF37] shrink-0" />
              <span className="text-white/50 text-xs uppercase tracking-wider">Winner</span>
            </div>
            <h3 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-medium text-white">
              {category.winner.name}
            </h3>
            {category.winner.subtitle && (
              <p className="text-white/60 text-sm mt-1">{category.winner.subtitle}</p>
            )}
          </div>
          {category.winner.url && (
            <a
              href={category.winner.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A] text-sm font-semibold transition-colors"
              data-testid={`winner-link-${category.id}`}
            >
              Visit
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>

      {/* Finalists */}
      <div className="p-6 sm:p-8">
        <p className="text-white/50 text-xs uppercase tracking-[0.25em] mb-4">Finalists</p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
          {category.finalists.map((f, i) => (
            <li
              key={i}
              className="text-white/75 text-sm flex items-start gap-2"
            >
              <span className="text-[#D4AF37]/60 mt-1">•</span>
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {category.note && (
          <div className="mt-6 p-4 rounded-lg bg-[#0A0A0A] border border-[#D4AF37]/20 flex gap-3">
            <Info className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
            <p className="text-white/70 text-xs leading-relaxed">{category.note}</p>
          </div>
        )}
      </div>
    </article>
  );
}
